import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { aiApi, charactersApi } from '../../lib/api';
import { getImagesByFolder, deleteImage, updateImageMetadata } from '../../lib/imageStorage';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
    Plus, Trash2, Sparkles, Image, Download, Loader2, Check, BookOpen, Clock,
    Folder, FolderOpen, ChevronRight, Star, MapPin, Edit2, X
} from 'lucide-react';

const IllustrationsPanel = ({ bookId, characters: propCharacters }) => {
    const { user, refreshUser } = useAuth();
    const [prompt, setPrompt] = useState('');
    const [style, setStyle] = useState('digital art, book illustration');
    const [tier, setTier] = useState('standard');
    const [conceptCount, setConceptCount] = useState(2);
    const [loading, setLoading] = useState(false);
    const [generationHistory, setGenerationHistory] = useState([]);
    const [activeTab, setActiveTab] = useState('folders');
    const [characters, setCharacters] = useState(propCharacters || []);
    const [folders, setFolders] = useState({});
    const [expandedFolder, setExpandedFolder] = useState(null);
    const [editingImage, setEditingImage] = useState(null);
    const [editLocation, setEditLocation] = useState({ chapter: '', page: '' });

    const styles = [
        'digital art, book illustration',
        'watercolor painting, soft tones',
        'fantasy art, detailed',
        'manga anime style',
        'realistic painting',
        'sketch, line art',
        'children\'s book illustration',
        'noir, dramatic lighting'
    ];

    // Load characters if not provided via props
    useEffect(() => {
        const loadCharacters = async () => {
            if (!propCharacters || propCharacters.length === 0) {
                try {
                    const res = await charactersApi.list(bookId);
                    setCharacters(res.data || []);
                } catch (error) {
                    console.error('Failed to load characters');
                }
            } else {
                setCharacters(propCharacters);
            }
        };
        loadCharacters();
    }, [bookId, propCharacters]);

    // Load folders (character images organized by character name)
    useEffect(() => {
        if (characters.length > 0) {
            const folderData = getImagesByFolder(bookId, characters);
            setFolders(folderData);
        }
    }, [bookId, characters]);

    // Refresh folders when switching to folders tab
    useEffect(() => {
        if (activeTab === 'folders' && characters.length > 0) {
            const folderData = getImagesByFolder(bookId, characters);
            setFolders(folderData);
        }
    }, [activeTab, bookId, characters]);

    // Load generation history
    useEffect(() => {
        const history = localStorage.getItem(`eskrybe-gen-history-${bookId}`);
        if (history) {
            setGenerationHistory(JSON.parse(history));
        }
    }, [bookId]);

    // Save history to localStorage
    useEffect(() => {
        localStorage.setItem(`eskrybe-gen-history-${bookId}`, JSON.stringify(generationHistory));
    }, [generationHistory, bookId]);

    const getCreditCost = () => {
        const baseCost = tier === 'standard' ? 5 : 10;
        return baseCost * conceptCount;
    };

    const handleGenerate = async () => {
        setLoading(true);
        const newGeneration = {
            id: Date.now().toString(),
            prompt,
            style,
            tier,
            conceptCount,
            images: [],
            timestamp: new Date().toISOString(),
            status: 'generating'
        };
        
        setGenerationHistory(prev => [newGeneration, ...prev]);
        
        try {
            const generatedImages = [];
            for (let i = 0; i < conceptCount; i++) {
                const variationPrompt = conceptCount > 1 
                    ? `${prompt}, ${style}, variation ${i + 1} of ${conceptCount}, unique interpretation`
                    : `${prompt}, ${style}`;
                    
                const res = await aiApi.illustrate({
                    prompt: variationPrompt,
                    tier,
                    book_id: bookId
                });
                
                generatedImages.push({
                    id: `${newGeneration.id}-${i}`,
                    url: res.data.image_url,
                    prompt: variationPrompt,
                    credits_used: res.data.credits_used
                });
            }
            
            setGenerationHistory(prev => prev.map(gen => 
                gen.id === newGeneration.id 
                    ? { ...gen, images: generatedImages, status: 'complete' }
                    : gen
            ));
            
            refreshUser();
            toast.success(`Generated ${conceptCount} concept${conceptCount > 1 ? 's' : ''}! Used ${getCreditCost()} credits`);
        } catch (error) {
            setGenerationHistory(prev => prev.map(gen => 
                gen.id === newGeneration.id 
                    ? { ...gen, status: 'failed', error: error.message }
                    : gen
            ));
            
            if (error.response?.status === 402) {
                toast.error('Insufficient credits');
            } else {
                toast.error('Failed to generate illustration');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteImage = (characterId, imageId) => {
        deleteImage(bookId, characterId, imageId);
        // Refresh folders
        const folderData = getImagesByFolder(bookId, characters);
        setFolders(folderData);
        toast.success('Image moved to trash (recoverable for 10 days)');
    };

    const handleUpdateLocation = () => {
        if (!editingImage) return;
        
        const locationStr = editLocation.chapter && editLocation.page 
            ? { chapter: editLocation.chapter, page: editLocation.page }
            : null;
        
        updateImageMetadata(bookId, editingImage.characterId, editingImage.id, {
            bookLocation: locationStr
        });
        
        // Refresh folders
        const folderData = getImagesByFolder(bookId, characters);
        setFolders(folderData);
        
        setEditingImage(null);
        setEditLocation({ chapter: '', page: '' });
        toast.success('Image location updated');
    };

    const deleteGeneration = (genId) => {
        setGenerationHistory(prev => prev.filter(gen => gen.id !== genId));
        toast.success('Generation removed');
    };

    const totalImages = Object.values(folders).reduce((sum, folder) => sum + folder.images.length, 0);

    return (
        <div className="p-8">
            <div className="mb-6">
                <h2 className="font-serif text-2xl font-semibold">Illustrations</h2>
                <p className="text-muted-foreground mt-1">Generate and manage AI illustrations for your book</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
                <Button 
                    variant={activeTab === 'folders' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('folders')}
                    data-testid="illustrations-tab-folders"
                >
                    <Folder className="h-4 w-4 mr-2" />
                    By Character ({Object.keys(folders).length})
                </Button>
                <Button 
                    variant={activeTab === 'generate' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('generate')}
                    data-testid="illustrations-tab-generate"
                >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate
                </Button>
                <Button 
                    variant={activeTab === 'history' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('history')}
                    data-testid="illustrations-tab-history"
                >
                    <Clock className="h-4 w-4 mr-2" />
                    History ({generationHistory.length})
                </Button>
                <Button 
                    variant={activeTab === 'all' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('all')}
                    data-testid="illustrations-tab-all"
                >
                    <BookOpen className="h-4 w-4 mr-2" />
                    All Images ({totalImages})
                </Button>
            </div>

            {/* Folders Tab - Character Images organized by folders */}
            {activeTab === 'folders' && (
                <div className="space-y-4">
                    {Object.keys(folders).length === 0 ? (
                        <div className="card-default p-12 text-center">
                            <Folder className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                            <h3 className="font-serif font-semibold mb-2">No character images yet</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Generate images for your characters from the Characters tab.<br />
                                They'll automatically appear here organized by character name.
                            </p>
                            <Button variant="outline" onClick={() => setActiveTab('generate')}>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Generate New Images
                            </Button>
                        </div>
                    ) : (
                        Object.entries(folders).map(([folderName, folderData]) => (
                            <div key={folderName} className="card-default overflow-hidden">
                                {/* Folder Header */}
                                <button
                                    className="w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors"
                                    onClick={() => setExpandedFolder(expandedFolder === folderName ? null : folderName)}
                                >
                                    {expandedFolder === folderName ? (
                                        <FolderOpen className="h-5 w-5 text-accent" />
                                    ) : (
                                        <Folder className="h-5 w-5 text-muted-foreground" />
                                    )}
                                    <span className="font-semibold flex-1 text-left">{folderName}</span>
                                    <Badge variant="secondary">{folderData.images.length} images</Badge>
                                    <ChevronRight className={`h-4 w-4 transition-transform ${expandedFolder === folderName ? 'rotate-90' : ''}`} />
                                </button>
                                
                                {/* Folder Content */}
                                {expandedFolder === folderName && (
                                    <div className="p-4 pt-0 border-t">
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                                            {folderData.images.map((img, idx) => (
                                                <div key={img.id} className="card-default p-2 relative group">
                                                    <div className="aspect-square rounded-lg overflow-hidden bg-muted mb-2 relative">
                                                        <img 
                                                            src={img.url} 
                                                            alt={img.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                        {img.isDefault && (
                                                            <div className="absolute top-2 left-2 bg-accent text-accent-foreground rounded-full p-1">
                                                                <Star className="h-3 w-3" />
                                                            </div>
                                                        )}
                                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                            <Button 
                                                                size="sm" 
                                                                variant="secondary"
                                                                onClick={() => {
                                                                    setEditingImage(img);
                                                                    setEditLocation({
                                                                        chapter: img.bookLocation?.chapter || '',
                                                                        page: img.bookLocation?.page || ''
                                                                    });
                                                                }}
                                                            >
                                                                <MapPin className="h-3 w-3" />
                                                            </Button>
                                                            <Button size="sm" variant="secondary" asChild>
                                                                <a href={img.url} download={`${img.title}.png`} target="_blank" rel="noopener noreferrer">
                                                                    <Download className="h-3 w-3" />
                                                                </a>
                                                            </Button>
                                                            <Button 
                                                                size="sm" 
                                                                variant="destructive"
                                                                onClick={() => handleDeleteImage(folderData.characterId, img.id)}
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <p className="text-xs font-medium truncate">{img.title}</p>
                                                    {img.bookLocation && (
                                                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                                            <MapPin className="h-3 w-3" />
                                                            {img.bookLocation.chapter}, Page {img.bookLocation.page}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Generate Tab */}
            {activeTab === 'generate' && (
                <div className="grid lg:grid-cols-2 gap-8">
                    <div className="card-default p-6">
                        <h3 className="font-semibold mb-4">Generate New Illustrations</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <Label>Describe your scene</Label>
                                <Textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder="A young wizard standing at the edge of an ancient forest, mystical light filtering through the trees..."
                                    rows={3}
                                    className="mt-1"
                                    data-testid="illustration-prompt"
                                />
                            </div>

                            <div>
                                <Label>Style</Label>
                                <select
                                    className="w-full h-10 rounded-md border border-input bg-background px-3 mt-1"
                                    value={style}
                                    onChange={(e) => setStyle(e.target.value)}
                                    data-testid="illustration-style"
                                >
                                    {styles.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <Label>Number of Concepts</Label>
                                <div className="grid grid-cols-4 gap-2 mt-2">
                                    {[1, 2, 3, 4].map(num => (
                                        <button
                                            key={num}
                                            className={`p-3 rounded-lg border text-center transition-colors ${
                                                conceptCount === num 
                                                    ? 'border-accent bg-accent/10 font-semibold' 
                                                    : 'border-border hover:border-accent/50'
                                            }`}
                                            onClick={() => setConceptCount(num)}
                                            data-testid={`concept-count-${num}`}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <Label>Quality Tier</Label>
                                <div className="grid grid-cols-2 gap-3 mt-2">
                                    <button
                                        className={`p-3 rounded-lg border text-left transition-colors ${
                                            tier === 'standard' 
                                                ? 'border-accent bg-accent/10' 
                                                : 'border-border hover:border-accent/50'
                                        }`}
                                        onClick={() => setTier('standard')}
                                        data-testid="tier-standard"
                                    >
                                        <div className="font-medium">Standard</div>
                                        <div className="text-xs text-muted-foreground">5 credits each - Fast</div>
                                    </button>
                                    <button
                                        className={`p-3 rounded-lg border text-left transition-colors ${
                                            tier === 'premium' 
                                                ? 'border-accent bg-accent/10' 
                                                : 'border-border hover:border-accent/50'
                                        }`}
                                        onClick={() => setTier('premium')}
                                        data-testid="tier-premium"
                                    >
                                        <div className="font-medium">Premium</div>
                                        <div className="text-xs text-muted-foreground">10 credits each - High Quality</div>
                                    </button>
                                </div>
                            </div>

                            <div className="p-3 bg-muted/50 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm">Total Cost:</span>
                                    <span className="font-semibold">{getCreditCost()} credits</span>
                                </div>
                                <div className="flex justify-between items-center mt-1">
                                    <span className="text-xs text-muted-foreground">Your balance:</span>
                                    <span className="text-xs">{user?.credits || 0} credits</span>
                                </div>
                            </div>

                            <Button 
                                onClick={handleGenerate} 
                                className="w-full" 
                                disabled={loading || !prompt.trim() || (user?.credits || 0) < getCreditCost()}
                                data-testid="generate-illustration-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Generating {conceptCount} concept{conceptCount > 1 ? 's' : ''}...
                                    </>
                                ) : (
                                    <>
                                        <Image className="h-4 w-4 mr-2" />
                                        Generate {conceptCount} Concept{conceptCount > 1 ? 's' : ''}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Quick Preview */}
                    <div>
                        {generationHistory.length > 0 && generationHistory[0].status === 'complete' ? (
                            <div className="card-default p-4">
                                <h4 className="font-semibold mb-3">Latest Generation</h4>
                                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                    "{generationHistory[0].prompt}"
                                </p>
                                <div className={`grid gap-3 ${generationHistory[0].images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                    {generationHistory[0].images.map((img, idx) => (
                                        <div key={img.id} className="relative group">
                                            <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                                                <img 
                                                    src={img.url} 
                                                    alt={`Concept ${idx + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                                                <Button size="sm" variant="secondary" asChild>
                                                    <a href={img.url} download={`concept-${idx + 1}.png`} target="_blank" rel="noopener noreferrer">
                                                        <Download className="h-3 w-3" />
                                                    </a>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : generationHistory.length > 0 && generationHistory[0].status === 'generating' ? (
                            <div className="card-default p-8 text-center">
                                <Loader2 className="h-12 w-12 mx-auto text-accent animate-spin mb-4" />
                                <h3 className="font-serif font-semibold mb-2">Generating...</h3>
                                <p className="text-sm text-muted-foreground">
                                    Creating {generationHistory[0].conceptCount} unique concept{generationHistory[0].conceptCount > 1 ? 's' : ''}
                                </p>
                            </div>
                        ) : (
                            <div className="card-default p-8 text-center">
                                <Image className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                                <h3 className="font-serif font-semibold mb-2">No illustrations yet</h3>
                                <p className="text-sm text-muted-foreground">
                                    Describe a scene and generate 1-4 unique concepts
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
                <div className="space-y-6">
                    {generationHistory.length === 0 ? (
                        <div className="card-default p-12 text-center">
                            <Clock className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                            <h3 className="font-serif font-semibold mb-2">No generation history</h3>
                            <p className="text-sm text-muted-foreground">
                                Your generated illustrations will appear here
                            </p>
                        </div>
                    ) : (
                        generationHistory.map((gen) => (
                            <div key={gen.id} className="card-default p-4">
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <p className="font-medium text-sm line-clamp-1">"{gen.prompt}"</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {new Date(gen.timestamp).toLocaleString()} - {gen.style} - {gen.tier}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge variant={gen.status === 'complete' ? 'default' : gen.status === 'generating' ? 'secondary' : 'destructive'}>
                                            {gen.status}
                                        </Badge>
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-8 w-8"
                                            onClick={() => deleteGeneration(gen.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                
                                {gen.status === 'complete' && (
                                    <div className={`grid gap-3 ${gen.images.length === 1 ? 'grid-cols-2' : gen.images.length === 2 ? 'grid-cols-2' : 'grid-cols-4'}`}>
                                        {gen.images.map((img, idx) => (
                                            <div key={img.id} className="relative group">
                                                <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                                                    <img 
                                                        src={img.url} 
                                                        alt={`Concept ${idx + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                                                    <Button size="sm" variant="secondary" asChild>
                                                        <a href={img.url} download={`concept-${idx + 1}.png`} target="_blank" rel="noopener noreferrer">
                                                            <Download className="h-3 w-3" />
                                                        </a>
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {gen.status === 'generating' && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Generating {gen.conceptCount} concept{gen.conceptCount > 1 ? 's' : ''}...
                                    </div>
                                )}
                                
                                {gen.status === 'failed' && (
                                    <p className="text-sm text-destructive">Generation failed. Please try again.</p>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* All Images Tab */}
            {activeTab === 'all' && (
                <div>
                    {totalImages === 0 ? (
                        <div className="card-default p-12 text-center">
                            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                            <h3 className="font-serif font-semibold mb-2">No images yet</h3>
                            <p className="text-sm text-muted-foreground">
                                Generate character illustrations to see them here
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-sm text-muted-foreground">
                                    {totalImages} illustration{totalImages !== 1 ? 's' : ''} across {Object.keys(folders).length} character{Object.keys(folders).length !== 1 ? 's' : ''}
                                </p>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                {Object.entries(folders).flatMap(([folderName, folderData]) =>
                                    folderData.images.map(img => (
                                        <div key={img.id} className="card-default p-2 group">
                                            <div className="aspect-square rounded-lg overflow-hidden bg-muted mb-2 relative">
                                                <img 
                                                    src={img.url} 
                                                    alt={img.title}
                                                    className="w-full h-full object-cover"
                                                />
                                                {img.isDefault && (
                                                    <div className="absolute top-2 left-2 bg-accent text-accent-foreground rounded-full p-1">
                                                        <Star className="h-3 w-3" />
                                                    </div>
                                                )}
                                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                    <Button size="sm" variant="secondary" asChild>
                                                        <a href={img.url} download={`${img.title}.png`} target="_blank" rel="noopener noreferrer">
                                                            <Download className="h-3 w-3" />
                                                        </a>
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="destructive"
                                                        onClick={() => handleDeleteImage(folderData.characterId, img.id)}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <p className="text-xs font-medium truncate">{img.title}</p>
                                            <p className="text-xs text-muted-foreground">{folderName}</p>
                                            {img.bookLocation && (
                                                <p className="text-xs text-accent flex items-center gap-1 mt-1">
                                                    <MapPin className="h-3 w-3" />
                                                    {img.bookLocation.chapter}, P.{img.bookLocation.page}
                                                </p>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Edit Location Dialog */}
            <Dialog open={!!editingImage} onOpenChange={(open) => !open && setEditingImage(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Set Book Location</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <p className="text-sm text-muted-foreground">
                            Specify where this image appears in your book.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Chapter</Label>
                                <Input
                                    value={editLocation.chapter}
                                    onChange={(e) => setEditLocation({...editLocation, chapter: e.target.value})}
                                    placeholder="e.g., Chapter 3"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Page</Label>
                                <Input
                                    value={editLocation.page}
                                    onChange={(e) => setEditLocation({...editLocation, page: e.target.value})}
                                    placeholder="e.g., 27"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setEditingImage(null)}>Cancel</Button>
                            <Button onClick={handleUpdateLocation}>Save Location</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default IllustrationsPanel;
